import { LoadingDetails } from "api/wms/models";
import { InfoLabel } from "components/common/infoLabel";
import { Avatar } from "components/miloDesignSystem/atoms/avatar";
import { Typography } from "components/miloDesignSystem/atoms/typography";
import { RightPanelSection } from "components/utils/drawer";
import styles from "../RightPanel.module.css";
import QRCode from "qrcode.react";
import { useSelector, useStateModal } from "hooks";
import { Select } from "components/miloDesignSystem/molecules/select";
import { MenuItemType } from "components/miloDesignSystem/atoms/menu/types";
import { dateFns } from "utilities";
import { getParsedDuration } from "pages/wms/shared/utils/getParsedDuration";
import { DatePicker } from "components/utils/datePicker";
import { TimePicker } from "components/utils/timePicker";
import { useLoadingPatchMutation, useLoadingPatchMutationWithoutParsing } from "api/wms/hooks";
import { LoadingDeparture } from "./LoadingDeparture";
import { LoadingStart } from "../../components/LoadingStart";
import { LoadingFinish } from "../../components/LoadingFinish";
import { EmptyValue } from "utilities/tableColumnsUtilities/createTableColumns/createTableColumns";
import {
  LoadingWorkspaceModal,
  LoadingWorkspaceState,
} from "./loadingWorkspaceModal/LoadingWorkspaceModal";
import { AsyncInput } from "components/utils";

interface Props {
  loading: LoadingDetails;
}

export const GeneralInfoSection = ({ loading }: Props) => {
  const ramps = useSelector(store => store.partials.wmsRamps);
  const rampMutation = useLoadingPatchMutationWithoutParsing();
  const estimatedTimeOfLoadingDateMutation = useLoadingPatchMutation();
  const estimatedTimeOfLoadingTimeMutation = useLoadingPatchMutation();
  const descriptionMutation = useLoadingPatchMutation();
  const loadingWorkspaceModal = useStateModal<LoadingWorkspaceState>();

  return (
    <>
      <RightPanelSection padding="px-3 pt-0 pb-2">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div>
            <InfoLabel title="rampa">
              <div>
                <Select
                  items={ramps.map(ramp => ({
                    text: ramp.name,
                    type: MenuItemType.TEXT,
                    value: ramp.id,
                  }))}
                  onChange={ramp => {
                    const foundRamp = ramps.find(_ramp => _ramp.id === ramp);
                    if (foundRamp) {
                      rampMutation.mutate({
                        id: loading.id,
                        toUpdate: {
                          ramp: {
                            id: foundRamp.id,
                            name: foundRamp.name,
                          },
                        },
                      });
                    }
                  }}
                  selected={loading.ramp?.id ?? ""}
                />
              </div>
            </InfoLabel>
            <InfoLabel title="planowany początek załadunku">
              <div className="d-flex align-items-center gap-2">
                <DatePicker
                  disabled={estimatedTimeOfLoadingDateMutation.isLoading}
                  look="common"
                  overwrites={{
                    popup: { className: styles.datePickerPopup },
                    input: { className: styles.datePickerInput },
                    container: { className: styles.datePickerInput },
                  }}
                  value={loading.estimatedTimeOfLoadingDate || ""}
                  onChange={date => {
                    if (!date) {
                      return estimatedTimeOfLoadingDateMutation.mutate({
                        id: loading.id,
                        toUpdate: {
                          estimatedTimeOfLoadingDate: null,
                        },
                      });
                    }
                    return estimatedTimeOfLoadingDateMutation.mutate({
                      id: loading.id,
                      toUpdate: {
                        estimatedTimeOfLoadingDate: dateFns.format(new Date(date), "yyyy-MM-dd"),
                      },
                    });
                  }}
                />
                <TimePicker
                  disabled={estimatedTimeOfLoadingTimeMutation.isLoading}
                  inProgress={estimatedTimeOfLoadingTimeMutation.isLoading}
                  look="common"
                  onBlur={time =>
                    estimatedTimeOfLoadingTimeMutation.mutate({
                      id: loading.id,
                      toUpdate: { estimatedTimeOfLoadingTime: time || null },
                    })
                  }
                  overwrites={{
                    container: { className: styles.formHeight },
                    input: { className: styles.formHeight },
                  }}
                  value={loading.estimatedTimeOfLoadingTime || ""}
                />
              </div>
            </InfoLabel>
            <InfoLabel title="opis">
              <div>
                <AsyncInput
                  disabled={descriptionMutation.isLoading}
                  inProgress={descriptionMutation.isLoading}
                  onChange={value =>
                    descriptionMutation.mutate({
                      id: loading.id,
                      toUpdate: { description: value },
                    })
                  }
                  overwrites={{
                    container: { className: styles.input },
                    input: { className: styles.input },
                  }}
                  placeholder="opis"
                  value={loading.description}
                />
              </div>
            </InfoLabel>
            <InfoLabel title="wyjazd">
              <LoadingDeparture loading={loading} />
            </InfoLabel>
            <div className="d-flex align-items-center gap-2 mb-2">
              <div className={styles.timeContainer}>
                <Typography color="neutralBlack48" fontSize="12" fontWeight="600">
                  start:
                </Typography>
                <LoadingStart fontSize="14" fontWeight="500" loading={loading} />
              </div>
              <div className={styles.timeContainer}>
                <Typography color="neutralBlack48" fontSize="12" fontWeight="600">
                  zakończenie:
                </Typography>
                <LoadingFinish fontSize="14" fontWeight="500" loading={loading} />
              </div>
              <div className={styles.timeContainer}>
                <Typography color="neutralBlack48" fontSize="12" fontWeight="600">
                  czas załadunku:
                </Typography>
                <Typography fontSize="14" fontWeight="600">
                  {loading.duration && Boolean(loading.duration.length)
                    ? getParsedDuration(loading.duration)
                    : "---"}
                </Typography>
              </div>
            </div>
            <InfoLabel title="kontrahenci">
              <Typography className={styles.suppliers} fontSize="14" fontWeight="700" noWrap>
                {loading.sellers && Boolean(loading.sellers.length)
                  ? loading.sellers.map(seller => seller.name).join(", ")
                  : "---"}
              </Typography>
            </InfoLabel>
            <InfoLabel className={styles.collaborators} title="osoby zaangażowane">
              <div className="d-flex flex-column gap-1">
                {loading.collaborators ? (
                  loading.collaborators.map(collaborator => (
                    <div
                      className="d-flex align-items-center gap-1 cursor-pointer"
                      onClick={() =>
                        loadingWorkspaceModal.open({
                          user: collaborator.id,
                          loading: loading.id,
                        })
                      }
                    >
                      <Avatar size="sm" user={collaborator} theme="light" />
                      <Typography className="ml-1" fontSize="12" fontWeight="700" noWrap>
                        {collaborator.firstName} {collaborator.lastName}
                      </Typography>
                    </div>
                  ))
                ) : (
                  <EmptyValue />
                )}
              </div>
            </InfoLabel>
          </div>
          <div className="d-flex flex-column gap-1">
            <div>
              <Typography color="neutralBlack48" fontSize="12" fontWeight="600">
                QR kod wydania
              </Typography>
            </div>
            <QRCode value={loading.qrCode} />
          </div>
        </div>
      </RightPanelSection>
      {loadingWorkspaceModal.isOpen &&
        loadingWorkspaceModal.state?.loading &&
        loadingWorkspaceModal.state.user && (
          <LoadingWorkspaceModal
            close={loadingWorkspaceModal.close}
            state={loadingWorkspaceModal.state}
          />
        )}
    </>
  );
};
